import { chain, thirdWebClient } from "@/app/libs/thirdweb";
import { uSPON, uSPONRedemption } from "@/contracts/addresses";
import { getContract, readContract, sendAndConfirmTransaction } from "thirdweb";
import { prepareContractCall } from "thirdweb/transaction";
import { Account } from "thirdweb/wallets";

export interface IRedeemUsponParams {
  shares: bigint;
  proof: `0x${string}`[];
  account: Account;
}

export const redeemUspon = async ({
  shares,
  proof,
  account,
}: IRedeemUsponParams) => {
  try {
    const uSponTokenAddress = uSPON;
    const uSponTokenContract = getContract({
      client: thirdWebClient,
      chain,
      address: uSponTokenAddress,
    });

    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: uSPONRedemption,
    });

    const approveRequest = await prepareContractCall({
      contract: uSponTokenContract,
      method: {
        inputs: [
          {
            internalType: "address",
            name: "spender",
            type: "address",
          },
          {
            internalType: "uint256",
            name: "value",
            type: "uint256",
          },
        ],
        name: "approve",
        outputs: [
          {
            internalType: "bool",
            name: "",
            type: "bool",
          },
        ],
        stateMutability: "nonpayable",
        type: "function",
      },
      params: [uSPONRedemption, shares],
    });

    const approveTx = await sendAndConfirmTransaction({
      transaction: approveRequest,
      account,
    });
    console.log(
      `uSPON token approval successful: ${approveTx.transactionHash}`
    );

    const transaction = prepareContractCall({
      contract,
      method: {
        inputs: [
          {
            internalType: "uint256",
            name: "_uSPONAmount",
            type: "uint256",
          },
          {
            internalType: "bytes32[]",
            name: "_merkleProof",
            type: "bytes32[]",
          },
        ],
        name: "redeemTokens",
        outputs: [],
        stateMutability: "nonpayable",
        type: "function",
      },
      params: [shares, proof],
    });

    // Send the transaction
    const transactionReceipt = await sendAndConfirmTransaction({
      transaction,
      account,
    });
    return transactionReceipt.transactionHash;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 *
 * @param address
 * @returns
 */
export const getUsponInfo = async (address: string) => {
  const response = await fetch(`/api/get-uspon-details`, {
    method: "POST",
    cache: "no-store",
    body: JSON.stringify({ address }),
  });
  return response.json();
};

export async function getUsponTotalSupply(): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: uSPON,
    });

    const totalSupply: bigint = await readContract({
      contract,
      method: {
        inputs: [],
        name: "totalSupply",
        outputs: [
          {
            internalType: "uint256",
            name: "",
            type: "uint256",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    });
    return totalSupply;
  } catch (error) {
    console.error("Error fetching total supply:", error);
    throw error;
  }
}

export async function getUsponBalance(account: Account): Promise<bigint> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: uSPON,
    });

    const balance: bigint = await readContract({
      contract,
      method: {
        type: "function",
        name: "balanceOf",
        inputs: [
          { name: "_account", type: "address", internalType: "address" },
        ],
        outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
        stateMutability: "view",
      },
      params: [account.address],
    });

    console.log(`uSPON Balance for ${account.address}:`, balance);
    return balance;
  } catch (error) {
    console.error("Error fetching balance:", error);
    throw error;
  }
}

export async function hasUserRedeemed(account: Account): Promise<boolean> {
  try {
    const contract = getContract({
      client: thirdWebClient,
      chain,
      address: uSPONRedemption,
    });

    const hasRedeemed: boolean = await readContract({
      contract,
      method: {
        inputs: [
          {
            internalType: "address",
            name: "user",
            type: "address",
          },
        ],
        name: "hasUserClaimed",
        outputs: [
          {
            internalType: "bool",
            name: "",
            type: "bool",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
      params: [account.address],
    });

    return hasRedeemed;
  } catch (error) {
    console.error("Error fetching hasUserRedeemed:", error);
    throw error;
  }
}
